import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getGroupTopics, getCourseCurriculumGroups, TopicDTO, CurriculumGroupDTO } from '../services/curriculumService';
import {

  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  LinearProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack,
  MenuBook,
  Assignment,
  Quiz,
  Schedule,
  Person,
  TrendingUp,
  Settings,
  PlayArrow,
  CheckCircle,
  RadioButtonUnchecked,
  MoreVert,
  ExpandMore,
  VideoLibrary,
  Description,
  Link as LinkIcon,
  School,
  Timer,
  Star,
} from '@mui/icons-material';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  userType: string;
}

interface TopicGroup {
  id: number;
  name: string;
  description: string;
  progress: number;
  totalTopics: number;
  completedTopics: number;
  lastAccessed: string;
  status: 'not_started' | 'in_progress' | 'completed';
  instructor: string;
  estimatedTime: string;
  difficulty: string;
  topics: Topic[];
  deliveryType?: string; // Course delivery type: SELF_TUTORED, SCHOOL_TEACHER, PRIVATE_TUTOR
}

interface Topic {
  id: number;
  name: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'completed';
  estimatedTime: string;
  resources: Resource[];
  learningObjectives: string[];
  prerequisites: string[];
}

// Map TopicDTO from API to Topic interface
const mapTopicDTOToTopic = (topicDTO: TopicDTO): Topic => {
  const status =
    topicDTO.progressStatus === 'COMPLETED' ? 'completed' :
      topicDTO.progressStatus === 'IN_PROGRESS' ? 'in_progress' :
        'not_started';

  return {
    id: topicDTO.id,
    name: topicDTO.title,
    description: topicDTO.description || '',
    status: status,
    estimatedTime: `${topicDTO.estimatedTimeMinutes || 0} min`,
    resources: [], // TODO: Add resources from API
    learningObjectives: topicDTO.learningObjectives || [],
    prerequisites: [], // TODO: Add prerequisites from API
  };
};

interface Resource {
  id: number;
  name: string;
  type: 'video' | 'document' | 'link' | 'quiz' | 'assignment';
  url: string;
  duration?: string;
  completed: boolean;
}

const SubjectDetails: React.FC<{ preview?: boolean }> = ({ preview = false }) => {
  const { courseId, subjectId } = useParams<{ courseId: string; subjectId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [topicGroup, setTopicGroup] = useState<TopicGroup | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopicGroupData = async () => {
      try {
        setLoading(true);

        if (preview) {
          // In preview mode, use a mock user
          setUser({
            id: 0,
            email: 'admin@preview.com',
            firstName: 'Preview',
            lastName: 'Admin',
            userType: 'SYSTEM_ADMIN'
          });
        } else {
          // Get user data from localStorage
          const userData = localStorage.getItem('user');
          if (userData) {
            setUser(JSON.parse(userData));
          }
        }

        const courseIdNum = parseInt(courseId || '1');
        const subjectIdNum = parseInt(subjectId || '1');

        // Fetch curriculum group details
        const groups = await getCourseCurriculumGroups(courseIdNum);
        const group = groups.find(g => g.id === subjectIdNum);

        // Fetch topics for this curriculum group
        const topicsDTO = await getGroupTopics(courseIdNum, subjectIdNum);

        // Map topics from API to component interface
        const topics = topicsDTO.map(mapTopicDTOToTopic);

        // Calculate progress
        const totalTopics = topics.length;
        const completedTopics = topics.filter(t => t.status === 'completed').length;
        const progress = group ? Math.round(group.progressPercentage || 0) : (totalTopics > 0
          ? Math.round((completedTopics / totalTopics) * 100)
          : 0);

        // Determine overall status
        const overallStatus =
          completedTopics === totalTopics && totalTopics > 0 ? 'completed' :
            completedTopics > 0 ? 'in_progress' :
              'not_started';

        // Calculate estimated time from topics
        const totalMinutes = topics.reduce((sum, topic) => {
          const minutes = parseInt(topic.estimatedTime.replace(/\s*min\s*/i, '')) || 0;
          return sum + minutes;
        }, 0);
        const estimatedTimeHours = Math.floor(totalMinutes / 60);
        const estimatedTimeMins = totalMinutes % 60;
        const estimatedTimeStr = estimatedTimeHours > 0
          ? `${estimatedTimeHours} hr ${estimatedTimeMins > 0 ? estimatedTimeMins + ' min' : ''}`.trim()
          : `${totalMinutes} min`;

        // Get delivery type from group
        const deliveryType = group?.deliveryType || 'SELF_TUTORED';
        const isSelfTutored = deliveryType === 'SELF_TUTORED';
        const instructorDisplay = isSelfTutored ? null : null; // Will be populated when we have instructor data

        // Create topic group object from curriculum data
        const topicGroupData: TopicGroup = {
          id: subjectIdNum,
          name: group?.name || 'Topic Group',
          description: group?.description || 'Scotland Mathematics curriculum topics',
          progress: progress,
          totalTopics: totalTopics,
          completedTopics: completedTopics,
          lastAccessed: new Date().toISOString().split('T')[0],
          status: overallStatus,
          instructor: instructorDisplay || '', // Empty for self-registered, or instructor name for school-based
          estimatedTime: estimatedTimeStr,
          difficulty: 'Intermediate', // TODO: Get from course API
          topics: topics,
          deliveryType: deliveryType, // Store for use in render
        };

        setTopicGroup(topicGroupData);
      } catch (error) {
        console.error('Error fetching topic group data:', error);
        // Fallback to empty topic group on error
        setTopicGroup({
          id: parseInt(subjectId || '1'),
          name: 'Topic Group',
          description: 'Scotland Mathematics curriculum',
          progress: 0,
          totalTopics: 0,
          completedTopics: 0,
          lastAccessed: '',
          status: 'not_started',
          instructor: '',
          estimatedTime: '',
          difficulty: '',
          topics: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTopicGroupData();
  }, [courseId, subjectId]);

  const handleBackToCourse = () => {
    if (preview) {
      navigate(`/admin/preview/courses/${courseId}`);
    } else {
      navigate(`/student-courses/${courseId}`);
    }
  };

  const handleStartTopic = (topic: Topic) => {
    if (preview) {
      navigate(`/admin/preview/courses/${courseId}/subjects/${subjectId}/topics/${topic.id}`);
    } else {
      navigate(`/student-courses/${courseId}/subjects/${subjectId}/topics/${topic.id}`);
    }
  };

  const handleResourceClick = (resource: Resource) => {
    // TODO: Open resource (video, document, etc.)
    console.log('Open resource:', resource.name);
  };

  const handleTopicGroupSettings = () => {
    // TODO: Navigate to topic group settings
    console.log('Topic group settings');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'not_started':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'in_progress':
        return <PlayArrow color="primary" />;
      case 'not_started':
        return <RadioButtonUnchecked color="disabled" />;
      default:
        return <RadioButtonUnchecked color="disabled" />;
    }
  };

  const getResourceIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <VideoLibrary color="primary" />;
      case 'document':
        return <Description color="secondary" />;
      case 'link':
        return <LinkIcon color="info" />;
      case 'quiz':
        return <Quiz color="warning" />;
      case 'assignment':
        return <Assignment color="error" />;
      default:
        return <Description color="default" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ mt: 4, px: 3, maxWidth: 'lg', mx: 'auto' }}>
        <Alert severity="error">Please log in to access topic group details.</Alert>
      </Box>
    );
  }

  if (!topicGroup) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Topic Group not found.</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ mt: 4, mb: 4, px: 3, maxWidth: 'lg', mx: 'auto' }}>
      {/* Breadcrumb Navigation */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => {
              if (preview) navigate('/admin/courses');
              else navigate('/student-dashboard');
            }}
            sx={{ cursor: 'pointer' }}
          >
            {preview ? 'Admin Dashboard' : 'Dashboard'}
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={handleBackToCourse}
            sx={{ cursor: 'pointer' }}
          >
            My Courses
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={handleBackToCourse}
            sx={{ cursor: 'pointer' }}
          >
            Mathematics
          </Link>
          <Typography color="text.primary">{topicGroup.name}</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <IconButton onClick={handleBackToCourse} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" component="h1" gutterBottom>
              {topicGroup.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
              {topicGroup.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip
                label={topicGroup.status.replace('_', ' ')}
                color={getStatusColor(topicGroup.status) as any}
                size="small"
              />
              <Chip
                label={topicGroup.difficulty}
                color="info"
                size="small"
              />
              <Chip
                label={topicGroup.estimatedTime}
                color="secondary"
                size="small"
              />
            </Box>
          </Grid>
          <Grid item>
            <Tooltip title="Topic Group Settings">
              <IconButton onClick={handleTopicGroupSettings}>
                <Settings />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Topic Group Progress */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Topic Group Progress</Typography>
            <Typography variant="body2" color="text.secondary">
              {topicGroup.progress}% Complete
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={topicGroup.progress}
            sx={{ height: 8, borderRadius: 4, mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Topics: {topicGroup.completedTopics}/{topicGroup.totalTopics} completed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last accessed: {topicGroup.lastAccessed}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Topics Section */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Learning Topics
              </Typography>
              {topicGroup.topics.map((topic, index) => (
                <Accordion key={topic.id} sx={{ mb: 1 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ mr: 2 }}>
                        {getStatusIcon(topic.status)}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">{topic.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {topic.description}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={topic.status.replace('_', ' ')}
                          size="small"
                          color={getStatusColor(topic.status) as any}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {topic.estimatedTime}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {topic.learningObjectives && topic.learningObjectives.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Learning Objectives:
                        </Typography>
                        <List dense>
                          {topic.learningObjectives.map((objective, idx) => (
                            <ListItem key={idx} sx={{ py: 0 }}>
                              <ListItemIcon sx={{ minWidth: 24 }}>
                                <Star sx={{ fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={objective}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {topic.resources && topic.resources.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Learning Resources:
                        </Typography>
                        <Grid container spacing={1}>
                          {topic.resources.map((resource) => (
                            <Grid item xs={12} sm={6} key={resource.id}>
                              <Card
                                variant="outlined"
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' },
                                  opacity: resource.completed ? 0.7 : 1,
                                }}
                                onClick={() => handleResourceClick(resource)}
                              >
                                <CardContent sx={{ py: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {getResourceIcon(resource.type)}
                                    <Box sx={{ flexGrow: 1 }}>
                                      <Typography variant="body2" noWrap>
                                        {resource.name}
                                      </Typography>
                                      {resource.duration && (
                                        <Typography variant="caption" color="text.secondary">
                                          {resource.duration}
                                        </Typography>
                                      )}
                                    </Box>
                                    {resource.completed && (
                                      <CheckCircle color="success" sx={{ fontSize: 16 }} />
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<PlayArrow />}
                        onClick={() => handleStartTopic(topic)}
                        disabled={topic.status === 'completed'}
                      >
                        {topic.status === 'completed' ? 'Review' : 'Start Learning'}
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Subject Information & Quick Actions */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={3}>
            {/* Topic Group Information */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Topic Group Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {topicGroup && topicGroup.deliveryType !== 'SELF_TUTORED' && topicGroup.instructor && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person color="primary" />
                        <Typography variant="body2">
                          <strong>Instructor:</strong> {topicGroup.instructor}
                        </Typography>
                      </Box>
                    )}
                    {topicGroup && topicGroup.deliveryType === 'SELF_TUTORED' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person color="primary" />
                        <Typography variant="body2">
                          <strong>Course Type:</strong> Self-tutored
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Timer color="primary" />
                      <Typography variant="body2">
                        <strong>Estimated Time:</strong> {topicGroup.estimatedTime}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUp color="primary" />
                      <Typography variant="body2">
                        <strong>Difficulty:</strong> {topicGroup.difficulty}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MenuBook color="primary" />
                      <Typography variant="body2">
                        <strong>Topics:</strong> {topicGroup.totalTopics}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Actions */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<PlayArrow />}
                      onClick={() => {
                        const nextTopic = topicGroup.topics.find(t => t.status === 'in_progress') ||
                          topicGroup.topics.find(t => t.status === 'not_started');
                        if (nextTopic) {
                          handleStartTopic(nextTopic);
                        }
                      }}
                    >
                      Continue Learning
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Assignment />}
                      onClick={() => {
                        if (preview) alert("Preview: Assignments not implemented");
                        else navigate(`/student-courses/${courseId}/subjects/${subjectId}/assignments`);
                      }}
                    >
                      View Assignments
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Quiz />}
                      onClick={() => {
                        if (preview) navigate(`/admin/preview/courses/${courseId}/subjects/${subjectId}/quiz`);
                        else navigate(`/student-courses/${courseId}/subjects/${subjectId}/quiz`);
                      }}
                    >
                      Take Quiz
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Settings />}
                      onClick={handleTopicGroupSettings}
                    >
                      Topic Group Settings
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Activity */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Activity
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Completed Linear Equations Quiz"
                        secondary="2 hours ago"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <VideoLibrary color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Watched Quadratic Functions Video"
                        secondary="Yesterday"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Assignment color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Submitted Polynomial Assignment"
                        secondary="3 days ago"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubjectDetails;
